import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { StarRating } from "@/components/StarRating";
import { toast } from "sonner";
import { UserAvatar } from "@/components/UserAvatar";

interface WriteReviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit?: (data: ReviewFormData) => void;
  reviewTarget: {
    id: string;
    name: string;
    avatarUrl?: string;
    serviceTitle: string;
  };
  defaultRating?: number; // Add this prop for pre-filling the rating
}

export interface ReviewFormData {
  rating: number;
  comment: string;
}

const reviewFormSchema = z.object({
  rating: z.number().min(1, "Please select a rating").max(5),
  comment: z
    .string()
    .min(10, "Review must be at least 10 characters")
    .max(1000, "Review cannot exceed 1000 characters"),
});

export function WriteReview({
  open,
  onOpenChange,
  onSubmit,
  reviewTarget,
  defaultRating = 0, // Default to 0 if not provided
}: WriteReviewProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<ReviewFormData>({
    resolver: zodResolver(reviewFormSchema),
    defaultValues: {
      rating: defaultRating, // Use the defaultRating prop here
      comment: "",
    },
  });

  // Set the rating when defaultRating prop changes
  useState(() => {
    if (defaultRating > 0) {
      form.setValue("rating", defaultRating);
    }
  });

  const handleFormSubmit = async (data: ReviewFormData) => {
    setIsSubmitting(true);
    
    try {
      onSubmit?.(data);
    } catch (error) {
      toast.error("Failed to submit review. Please try again.");
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form when modal opens/closes
  const handleOpenChange = (open: boolean) => {
    if (open && defaultRating > 0) {
      // Set the form value when the modal opens and defaultRating is provided
      form.setValue("rating", defaultRating);
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-center">Write a Review</DialogTitle>
          <DialogDescription className="text-center">
            Share your experience with {reviewTarget.name}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-4 py-2">
          <UserAvatar
            userId={reviewTarget.id}
            avatarUrl={reviewTarget.avatarUrl}
            fallback={reviewTarget.name.charAt(0)}
            size="lg"
          />
          <div>
            <h3 className="font-medium">{reviewTarget.name}</h3>
            <p className="text-sm text-gray-500">{reviewTarget.serviceTitle}</p>
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="rating"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Rating</FormLabel>
                  <FormControl>
                    <div className="flex justify-center py-2">
                      <StarRating
                        value={field.value}
                        onChange={field.onChange}
                        size="lg"
                      />
                    </div>
                  </FormControl>
                  <FormMessage className="text-center" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="comment"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Your Review</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Share the details of your experience..."
                      className="min-h-[120px]"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Your review will help others make informed decisions.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => onOpenChange(false)}
                type="button"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-anyskill-purple hover:bg-anyskill-purple/90"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit Review"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
